import { FunctionComponent, ReactElement, useMemo, useState } from "react";

import {
    Badge,
    Button,
    Chip,
    MenuItem,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import {
    DataGrid,
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridRowParams,
} from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";

import {
    useGetIngredientCategories,
    useGetIngredients,
    useGetUnmatchedIngredients,
} from "../../hooks";
import { IIngredient, IngredientStatus } from "../../sdk/types/api";

import { ImportIngredientsDialog } from "./ImportIngredientsDialog";
import {
    ingredientBaseUnitNames,
    ingredientQueryKeys,
    ingredientStatusNames,
} from "./constants";

const Root = styled("section")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(1)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: ${({ theme }) => theme.spacing(2, 1)};
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const Actions = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const Filters = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(0, 1)};
`;

const Aliases = styled("div")`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const IngredientsScreen: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [status, setStatus] = useState<IngredientStatus | "">("");
    const [pagination, setPagination] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 25,
    });
    const [importOpen, setImportOpen] = useState(false);

    const parameters = {
        q: query || undefined,
        categoryId: categoryId || undefined,
        status: status || undefined,
        page: pagination.page + 1,
        limit: pagination.pageSize,
    };
    const ingredientsQuery = useGetIngredients(
        [ingredientQueryKeys.list, JSON.stringify(parameters)],
        parameters,
        { keepPreviousData: true },
    );
    const categoriesQuery = useGetIngredientCategories(ingredientQueryKeys.categories);
    const unmatchedQuery = useGetUnmatchedIngredients(
        [ingredientQueryKeys.unmatched, 1, 1],
        { page: 1, limit: 1 },
    );

    const categoryNameById = useMemo(
        () =>
            new Map(
                (categoriesQuery.data ?? []).map((category) => [
                    category.id,
                    `${category.icon ?? ""} ${category.name}`.trim(),
                ]),
            ),
        [categoriesQuery.data],
    );

    const columns: GridColDef<IIngredient>[] = [
        { field: "name", headerName: "Name", flex: 0.8, minWidth: 180 },
        {
            field: "categoryId",
            headerName: "Category",
            flex: 0.6,
            valueGetter: ({ row }) => categoryNameById.get(row.categoryId ?? "") ?? "Other",
        },
        {
            field: "baseUnit",
            headerName: "Base unit",
            flex: 0.5,
            valueGetter: ({ row }) => ingredientBaseUnitNames[row.baseUnit],
        },
        {
            field: "aliases",
            headerName: "Aliases",
            flex: 1.2,
            sortable: false,
            renderCell: ({ row }: GridRenderCellParams<IIngredient>) => (
                <Aliases>
                    {row.aliases.slice(0, 4).map((alias) => (
                        <Chip key={alias} label={alias} size="small" variant="outlined" />
                    ))}
                    {row.aliases.length > 4 && (
                        <Chip label={`+${row.aliases.length - 4}`} size="small" />
                    )}
                </Aliases>
            ),
        },
        {
            field: "isStaple",
            headerName: "Staple",
            flex: 0.3,
            valueGetter: ({ row }) => (row.isStaple ? "Yes" : ""),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.4,
            renderCell: ({ row }: GridRenderCellParams<IIngredient>) => (
                <Chip
                    size="small"
                    label={ingredientStatusNames[row.status]}
                    color={row.status === IngredientStatus.ACTIVE ? "success" : "default"}
                />
            ),
        },
    ];

    const handleRowClick = (params: GridRowParams<IIngredient>) => {
        navigate(`/ingredients/${params.row.id}`);
    };

    const resetPage = () => setPagination((current) => ({ ...current, page: 0 }));

    return (
        <Root>
            <TitleContainer>
                <Title>Ingredients</Title>
                <Actions>
                    <Link to="/ingredients/unmatched">
                        <Badge
                            color="warning"
                            badgeContent={unmatchedQuery.data?.total ?? 0}
                            max={999}
                        >
                            <Button variant="outlined">Unmatched names</Button>
                        </Badge>
                    </Link>
                    <Button variant="outlined" onClick={() => setImportOpen(true)}>
                        Import
                    </Button>
                    <Link to="/ingredients/new">
                        <Button variant="contained">New ingredient</Button>
                    </Link>
                </Actions>
            </TitleContainer>

            <Filters>
                <TextField
                    size="small"
                    label="Search name or alias"
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        resetPage();
                    }}
                    sx={{ minWidth: 280 }}
                />
                <TextField
                    select
                    size="small"
                    label="Category"
                    value={categoryId}
                    onChange={(event) => {
                        setCategoryId(event.target.value);
                        resetPage();
                    }}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">All categories</MenuItem>
                    {(categoriesQuery.data ?? []).map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                            {`${category.icon ?? ""} ${category.name}`.trim()}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={status}
                    onChange={(event) => {
                        setStatus(event.target.value === "" ? "" : Number(event.target.value));
                        resetPage();
                    }}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={IngredientStatus.ACTIVE}>Active</MenuItem>
                    <MenuItem value={IngredientStatus.INACTIVE}>Inactive</MenuItem>
                </TextField>
            </Filters>

            <DataGrid
                autoHeight
                rows={ingredientsQuery.data?.records ?? []}
                rowCount={ingredientsQuery.data?.total ?? 0}
                columns={columns}
                loading={ingredientsQuery.isLoading || ingredientsQuery.isFetching}
                paginationMode="server"
                paginationModel={pagination}
                onPaginationModelChange={setPagination}
                pageSizeOptions={[25, 50, 100]}
                onRowClick={handleRowClick}
                disableRowSelectionOnClick
                disableColumnSelector
                disableDensitySelector
            />

            <ImportIngredientsDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
            />
        </Root>
    );
};
